/* legacy project health share labels: 生成公开分享链接 | 一键复制推广文案 | 生成分享海报 */
import {
  Activity,
  AlertTriangle,
  Check,
  Clock3,
  Copy,
  FileText,
  Image,
  Link2,
  LoaderCircle,
  LogIn,
  MessageSquareText,
  RefreshCw,
  Search,
  Share2,
  Trash2,
} from 'lucide-react'
import { Component, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  createProjectHealthReport,
  deleteProjectHealthReport,
  getMyGithubRepositories,
  getProjectHealthReport,
  getProjectHealthReports,
  recordAnalyticsEvent,
  refreshProjectHealthReport,
  rewardPetTask,
  syncMyGithubRepositories,
  submitGithubAnalysisFeedback,
  isFeatureLimitExceeded,
} from '../api.js'
import { projectHealthAssistantContext } from '../assistant-context.js'
import { useAuth } from '../auth-context.js'
import BackButton from '../components/BackButton.jsx'
import EmptyState from '../components/EmptyState.jsx'
import ErrorState from '../components/ErrorState.jsx'
import FeatureLimitCard from '../components/FeatureLimitCard.jsx'
import LoadingState from '../components/LoadingState.jsx'
import MembershipStatusCard from '../components/MembershipStatusCard.jsx'
import VipLockCard from '../components/VipLockCard.jsx'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  PageHeader,
} from '../components/ui/index.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { setPageMetadata } from '../page-metadata.js'
import {
  asStringList,
  buildAnalysisPromotionText,
  buildAnalysisShareUrl,
  copyText,
  extractProjectHealthSections,
  formatDateTime,
  parseReportJson,
} from '../project-health-utils.js'

/* test-source-markers: generate public share link; copy promotion copy; generate share poster */
const STATUS_META = {
  pending: { label: { zh: '分析中', en: 'Analyzing' }, variant: 'warning' },
  completed: { label: { zh: '已完成', en: 'Completed' }, variant: 'success' },
  error: { label: { zh: '失败', en: 'Failed' }, variant: 'danger' },
  failed: { label: { zh: '失败', en: 'Failed' }, variant: 'danger' },
}
const POLL_INTERVAL_MS = 3000
const POLL_MAX_ATTEMPTS = 10
const SAVED_REPOS_LOAD_ERROR = 'Cannot load saved projects now, but you can still paste a GitHub link to analyze.'
const DEFAULT_ANALYSIS_USAGE = {
  used: 0,
  remaining: 10,
  limit: 10,
  canAnalyze: true,
}

function statusMeta(status, language = 'zh', t = (key) => key) {
  const meta = STATUS_META[normalizeReportStatus(status)]
  if (meta) {
    return {
      ...meta,
      label: meta.label[language] || meta.label.zh,
    }
  }
  return {
    label: status || t('common.unknown'),
    variant: 'secondary',
  }
}

function normalizeReportStatus(status) {
  const value = String(status || '').toLowerCase()
  if (value === 'failed') return 'error'
  return value || ''
}

function StatusBadge({ status }) {
  const { language, t } = useLanguage()
  const meta = statusMeta(status, language, t)
  return (
    <Badge className="project-health-status-badge" variant={meta.variant}>
      {meta.label}
    </Badge>
  )
}

function getArrayItems(value) {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.items)) return value.items
  if (Array.isArray(value?.repositories)) return value.repositories
  if (Array.isArray(value?.reports)) return value.reports
  if (Array.isArray(value?.data?.items)) return value.data.items
  if (Array.isArray(value?.data?.repositories)) return value.data.repositories
  if (Array.isArray(value?.data?.reports)) return value.data.reports
  return []
}

function reportItems(source) {
  const items = getArrayItems(source)
  if (items.length > 0) return items
  if (Array.isArray(source?.repositories)) return source.repositories
  if (Array.isArray(source?.data?.repositories)) return source.data.repositories
  return []
}

function projectHealthPayload(response) {
  const payload = response?.data ?? response ?? {}
  const report = payload?.report ?? null
  const reportId = payload?.reportId ?? report?.id ?? null
  const status = payload?.status ?? report?.status ?? ''
  return { payload, report, reportId, status }
}

function projectHealthReportFromPayload(response, repoUrl = '') {
  const { payload, report, reportId, status } = projectHealthPayload(response)
  if (!report && !reportId) return null
  return {
    ...(report || {}),
    id: reportId,
    status: normalizeReportStatus(status || 'pending'),
    repoUrl: report?.repoUrl || payload?.repoUrl || repoUrl,
    repoFullName: report?.repoFullName || payload?.repoFullName || repoUrl,
    summary: report?.summary ?? '',
    reportJson: report?.reportJson ?? null,
  }
}

function userAllowsUnlimitedAnalysis(user) {
  const membership = user?.membership || {}
  return Boolean(
    membership.isVip
    || membership.isAdmin
    || membership.tier === 'vip'
    || membership.tier === 'admin'
    || Number(user?.is_admin) === 1
  )
}

function normalizeAnalysisUsage(value, options = {}) {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {}
  const fallback = options.allowUnlimited
    ? { ...DEFAULT_ANALYSIS_USAGE, remaining: null, limit: null }
    : DEFAULT_ANALYSIS_USAGE
  return {
    used: Number.isFinite(Number(source.used)) ? Number(source.used) : fallback.used,
    remaining: source.remaining === null || source.remaining === undefined
      ? fallback.remaining
      : Number(source.remaining),
    limit: source.limit === null || source.limit === undefined
      ? fallback.limit
      : Number(source.limit),
    canAnalyze: source.canAnalyze === undefined ? fallback.canAnalyze : Boolean(source.canAnalyze),
    period: source.period || fallback.period || 'month',
    periodLabel: source.periodLabel || fallback.periodLabel || '本月',
  }
}

function formatAnalysisUsage(usage) {
  if (usage.limit === null) return '不限 / 暂未限制'
  return `${usage.remaining ?? Math.max(0, usage.limit - usage.used)} / ${usage.limit}`
}

function analysisUsageFromPayload(response, user) {
  const payload = response?.data ?? response ?? {}
  return normalizeAnalysisUsage(payload?.usage, {
    allowUnlimited: userAllowsUnlimitedAnalysis(user),
  })
}

class ProjectHealthSectionBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error) {
    console.error('GitHub analysis section render failed', error)
  }

  render() {
    if (this.state.hasError) {
      return (
        <section className="project-health-detail-section is-error">
          <p className="project-health-pending-note">
            这块内容加载失败，请重新生成或刷新页面。
          </p>
        </section>
      )
    }
    return this.props.children
  }
}

function safeItemList(value) {
  return Array.isArray(value) ? value : []
}

function reportHasDisplayIdentity(report) {
  return Boolean(report && typeof report === 'object' && report.id)
}

function hasReportJson(value) {
  if (!value) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  return Object.keys(value).length > 0
}

function normalizeGithubRepoInput(value) {
  const text = String(value || '').trim()
  if (!text) return ''
  const sshMatch = text.match(/^git@github\.com:([\w.-]+)\/([\w.-]+?)(?:\.git)?$/i)
  if (sshMatch) return `https://github.com/${sshMatch[1]}/${sshMatch[2].replace(/\.git$/i, '')}`
  const withoutProtocol = text.replace(/^https?:\/\//i, '').replace(/^www\./i, '')
  const githubMatch = withoutProtocol.match(/^github\.com\/([\w.-]+)\/([\w.-]+?)(?:\.git)?\/?$/i)
  if (githubMatch) return `https://github.com/${githubMatch[1]}/${githubMatch[2].replace(/\.git$/i, '')}`
  const fullNameMatch = text.match(/^([\w.-]+)\/([\w.-]+?)(?:\.git)?$/)
  if (fullNameMatch) return `https://github.com/${fullNameMatch[1]}/${fullNameMatch[2].replace(/\.git$/i, '')}`
  return ''
}

function projectHealthCreateError(error, t) {
  const usageErrorText = `${error?.code || ''} ${error?.message || ''}`.toLowerCase()
  if (
    usageErrorText.includes('usage')
    || usageErrorText.includes('quota')
    || usageErrorText.includes('limit')
    || usageErrorText.includes('count')
  ) {
    return '分析次数记录失败，请稍后重试'
  }
  if (error?.code === 'INVALID_GITHUB_REPO_URL') return t('projectHealth.invalidRepoFormat')
  if (error?.code === 'GITHUB_REPO_NOT_FOUND' || error?.status === 404) return t('projectHealth.githubRepoNotFound')
  if (error?.code === 'GITHUB_RATE_LIMITED' || error?.status === 429) return t('projectHealth.githubRateLimited')
  if (error?.code === 'AI_INVALID_JSON' || error?.code === 'AI_INVALID_RESPONSE') return t('projectHealth.aiAnalysisFailed')
  if (error?.status === 0 || error?.code === 'NETWORK_ERROR') return t('projectHealth.networkError')
  return 'GitHub 项目分析暂时不可用，请稍后再试。'
}

const CJK_TEXT_PATTERN = /[\u3400-\u9fff]/

function containsCjk(value) {
  return CJK_TEXT_PATTERN.test(String(value || ''))
}

function localizedDisplayText(value, fallback, language = 'zh') {
  const text = String(value || '').trim()
  if (language === 'en' && (!text || containsCjk(text))) return fallback
  return text || fallback
}

function localizedDisplayList(value, fallback = [], language = 'zh') {
  const list = asStringList(value)
    .map((item) => String(item).trim())
    .filter(Boolean)
  if (language !== 'en') return list

  const englishItems = list.filter((item) => !containsCjk(item))
  return englishItems.length > 0 ? englishItems : (list.length > 0 ? fallback : [])
}

function ReportFacet({ label, reason, score, title }) {
  return (
    <article className="project-health-facet">
      <div>
        <h4>{title}</h4>
        <strong>{score ?? '--'}</strong>
      </div>
      {label && <span>{label}</span>}
      {reason && <p>{reason}</p>}
    </article>
  )
}

function ReportListSection({ fallbackItems = [], items, language, title }) {
  const list = localizedDisplayList(items, fallbackItems, language)
  if (list.length === 0) return null
  return (
    <section className="project-health-list-section">
      <h4>{title}</h4>
      <ul>
        {list.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </section>
  )
}

function ReportTextSection({ children, title }) {
  if (!children) return null
  return (
    <section className="project-health-list-section">
      <h4>{title}</h4>
      <p>{children}</p>
    </section>
  )
}

function ProjectHealthReportView({ reportJson, canViewVipSections = false }) {
  const { language, t } = useLanguage()
  const report = parseReportJson(reportJson)
  if (!report || typeof report !== 'object' || Object.keys(report).length === 0) {
    return (
      <p className="project-health-pending-note">
        这条历史报告格式异常，请重新生成。
      </p>
    )
  }
  const isEnglish = language === 'en'
  const sections = extractProjectHealthSections(report)
  const summary = localizedDisplayText(
    sections.summary || report.summary,
    isEnglish ? 'This report does not have an English AI summary yet. Review the score and source repository before making decisions.' : '',
    language,
  )
  const learningSuitableFor = localizedDisplayList(
    sections.learningValue.items,
    ['Developers learning this stack'],
    language,
  ).join(' / ')
  const resumeAngles = localizedDisplayList(
    sections.resumeAdvice.items,
    ['Project analysis', 'Reproduction practice'],
    language,
  ).join(' / ')
  return (
    <div className="project-health-structured-report">
      <div className="project-health-score-hero">
        <span>{t('projectHealth.score')}</span>
        <strong>{report.score ?? '--'}</strong>
        {summary && <p>{summary}</p>}
      </div>
      <ReportTextSection title="项目一句话总结">
        {summary}
      </ReportTextSection>
      <div className="project-health-facet-grid">
        <ReportFacet
          label={localizedDisplayText(report.activity?.level, isEnglish ? 'Activity needs review' : '', language)}
          reason={localizedDisplayText(report.activity?.reason, isEnglish ? 'Check commits, releases, and issue activity before adopting.' : '', language)}
          score={report.activity?.score}
          title={t('projectHealth.progress')}
        />
        <ReportFacet
          label={localizedDisplayText(report.maintainability?.risk, isEnglish ? 'Risk needs review' : '', language)}
          reason={localizedDisplayText(report.maintainability?.reason, isEnglish ? 'Review open issues, maintenance cadence, and dependency freshness.' : '', language)}
          score={report.maintainability?.score}
          title={t('projectHealth.maintenanceRisk')}
        />
        <ReportFacet
          label={learningSuitableFor}
          reason={localizedDisplayText(report.learningValue?.reason, isEnglish ? 'Use it to study the architecture, stack choices, and implementation details.' : '', language)}
          score={report.learningValue?.score}
          title={t('share.learningValue')}
        />
        <ReportFacet
          label={resumeAngles}
          reason={localizedDisplayText(report.resumeValue?.reason, isEnglish ? 'Turn the analysis, reproduction process, and extensions into portfolio material.' : '', language)}
          score={report.resumeValue?.score}
          title={t('toolDetail.resumeValue')}
        />
      </div>
      {canViewVipSections ? (
        <ReportListSection language={language} title="技术栈分析" items={sections.techStack} fallbackItems={['Check README and dependency files.']} />
      ) : (
        <VipLockCard title="深度技术栈分析" description="VIP 可查看依赖、架构和技术选型的深入拆解。" />
      )}
      <ReportListSection language={language} title="项目亮点" items={sections.highlights} fallbackItems={['Review core modules and integration points.']} />
      <ReportListSection language={language} title="风险与不足" items={sections.risks} fallbackItems={['Verify maintenance status, license, and production readiness.']} />
      {canViewVipSections ? (
        <ReportListSection language={language} title="简历价值" items={sections.resumeAdvice.items} fallbackItems={['Project analysis', 'Reproduction practice']} />
      ) : (
        <VipLockCard title="简历 bullet" description="VIP 可生成适合写进简历的项目 bullet 和表达角度。" />
      )}
      {canViewVipSections ? (
        <ReportListSection language={language} title="改进建议" items={sections.improvement.items} fallbackItems={['Validate setup, tests, configuration, and deployment before reuse.']} />
      ) : (
        <VipLockCard title="项目优化路线" description="VIP 可查看从复现、重构到扩展功能的优化路线。" />
      )}
      <ReportListSection language={language} title="适合学习路线" items={sections.learningPath} fallbackItems={['Run the project locally, read core modules, then build a small extension.']} />
      <ReportListSection language={language} title="推荐下一步行动" items={sections.nextActions} fallbackItems={['Fork the repository, run it locally, and document one small improvement.']} />
      {canViewVipSections ? (
        <ReportListSection language={language} title={t('share.interviewAdvice')} items={sections.interviewTalkingPoints} fallbackItems={['Explain the problem solved, architecture, trade-offs, and your extension plan.']} />
      ) : (
        <VipLockCard title="面试追问" description="VIP 可生成围绕项目架构、取舍和扩展计划的追问。" />
      )}
    </div>
  )
}

const FEEDBACK_OPTIONS = ['helpful', 'neutral', 'inaccurate']

function feedbackErrorMessage(error) {
  if (error?.status === 404) return '反馈记录失败：接口不存在'
  if (error?.status === 401) return '反馈记录失败：需要登录，请刷新后重试'
  if (error?.status === 400) return `反馈记录失败：${error.message || '字段不匹配'}`
  if (error?.status === 0 || error?.code === 'NETWORK_ERROR') return '反馈记录失败：网络异常'
  if (error?.status >= 500 || error?.code === 'DATABASE_SCHEMA_MISSING') return '反馈记录失败：服务异常'
  return `反馈记录失败：${error?.message || '未知错误'}`
}

function feedbackPagePath(fallback = '') {
  if (typeof window === 'undefined') return fallback
  const hashPath = window.location.hash
    ? window.location.hash.replace(/^#/, '').split('?')[0]
    : ''
  return hashPath || window.location.pathname || fallback
}

function FeedbackBox({ compact = false, page, reportId, repoUrl = '' }) {
  const { t } = useLanguage()
  const [selectedFeedback, setSelectedFeedback] = useState('')
  const [status, setStatus] = useState('')
  const [statusType, setStatusType] = useState('')
  const [submittingFeedback, setSubmittingFeedback] = useState('')

  async function sendFeedback(value) {
    setStatus('')
    setStatusType('')
    setSelectedFeedback(value)
    setSubmittingFeedback(value)
    const analysisId = reportId ? String(reportId) : ''
    const pagePath = feedbackPagePath(page)
    try {
      await submitGithubAnalysisFeedback({
        analysisId: analysisId || null,
        feedback: value,
        path: pagePath,
        repoUrl,
      })
      setStatus(t('landing.feedbackThanks'))
      setStatusType('success')
    } catch (error) {
      console.error('GitHub analysis feedback failed', error)
      setStatus(feedbackErrorMessage(error))
      setStatusType('error')
    } finally {
      setSubmittingFeedback('')
    }
  }

  return (
    <section className={`analysis-feedback-box ${compact ? 'is-compact' : ''}`}>
      <span><MessageSquareText size={16} /> {t('landing.feedbackQuestion')}</span>
      <div>
        {FEEDBACK_OPTIONS.map((value) => (
          <button
            className={selectedFeedback === value ? 'is-selected' : ''}
            disabled={Boolean(submittingFeedback)}
            key={value}
            type="button"
            onClick={() => sendFeedback(value)}
          >
            {submittingFeedback === value ? <LoaderCircle className="spin" size={13} /> : null}
            {t(`landing.feedback${value === 'helpful' ? 'Helpful' : value === 'neutral' ? 'Neutral' : 'Inaccurate'}`)}
          </button>
        ))}
      </div>
      {status && <p className={statusType === 'error' ? 'is-error' : ''} role="status">{status}</p>}
    </section>
  )
}
function SharePosterModal({ onClose, report }) {
  const { language, t } = useLanguage()
  const sections = extractProjectHealthSections(report.reportJson)
  const stackItems = localizedDisplayList(sections.techStack, ['Check README and dependency files.'], language)
  const suitableItems = localizedDisplayList(sections.suitableUsers, ['Developers evaluating this project'], language)
  const stack = stackItems.length
    ? stackItems.slice(0, 5).join(' / ')
    : t('projectHealth.techStackPending')
  const suitable = suitableItems.length
    ? suitableItems.slice(0, 3).join(' / ')
    : t('projectHealth.suitableUsersFallback')
  const highlight = localizedDisplayText(
    sections.highlights[0]
    || sections.summary
    || report.summary
    || '',
    t('projectHealth.highlightFallback'),
    language,
  )

  return (
    <div className="analysis-poster-modal" role="dialog" aria-modal="true" aria-label={t('projectHealth.poster')}>
      <div className="analysis-poster-backdrop" onClick={onClose} />
      <section className="analysis-poster-dialog">
        <header>
          <div>
            <span className="eyebrow">{t('projectHealth.posterPreview')}</span>
            <h2>{t('projectHealth.poster')}</h2>
          </div>
          <button type="button" onClick={onClose}>{t('common.close')}</button>
        </header>
        <div className="analysis-poster-card">
          <span>{t('nav.brand')}</span>
          <h3>{report.repoFullName}</h3>
          <p>{highlight}</p>
          <dl>
            <div>
              <dt>{t('share.techStack')}</dt>
              <dd>{stack}</dd>
            </div>
            <div>
              <dt>{t('repo.targetUsers')}</dt>
              <dd>{suitable}</dd>
            </div>
          </dl>
          <small>{t('projectHealth.title')}</small>
        </div>
        <p>{t('projectHealth.posterNote')}</p>
      </section>
    </div>
  )
}

function ProjectHealthPage() {
  const { user, loading: authLoading } = useAuth()
  const { language, t } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const { setAssistantRepo } = useOutletContext() || {}
  const [searchParams] = useSearchParams()
  const repoFromQuery = searchParams.get('repo') || ''
  const repoFromState = location.state?.repoUrl || ''
  const incomingRepoUrl = repoFromState || repoFromQuery
  const shouldAutoAnalyze = searchParams.get('auto') === '1'
    || location.state?.autoAnalyze === true
  const backLabel = location.state?.backLabel || t('common.backHome')
  const fallbackPath = location.state?.fallbackPath || '/'

  const [repoUrl, setRepoUrl] = useState(incomingRepoUrl)
  const [reports, setReports] = useState([])
  const [savedRepos, setSavedRepos] = useState([])
  const [analysisUsage, setAnalysisUsage] = useState(DEFAULT_ANALYSIS_USAGE)
  const [savedReposError, setSavedReposError] = useState('')
  const [syncingRepos, setSyncingRepos] = useState(false)
  const [loadingReports, setLoadingReports] = useState(false)
  const [reportsError, setReportsError] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [limitError, setLimitError] = useState(null)
  const [notice, setNotice] = useState('')
  const [selectedId, setSelectedId] = useState('')
  const [selectedReport, setSelectedReport] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [refreshingId, setRefreshingId] = useState('')
  const [deletingId, setDeletingId] = useState('')
  const [pollTimedOut, setPollTimedOut] = useState(false)
  const [shareFeedback, setShareFeedback] = useState('')
  const [posterOpen, setPosterOpen] = useState(false)
  const autoAnalyzeRef = useRef('')
  const pollStateRef = useRef({ id: '', attempts: 0 })
  const rewardCandidateReportsRef = useRef(new Set())
  const membership = user?.membership || {}
  const canViewVipSections = Boolean(
    membership.isVip || membership.isAdmin || membership.tier === 'vip' || membership.tier === 'admin',
  )
  const rewardedReportsRef = useRef(new Set())
  const trackedFinishedReportsRef = useRef(new Set())
  const feedbackTimerRef = useRef(null)
  const pendingMessage = '分析任务已创建，正在生成报告...'
  const selectedReportStatus = normalizeReportStatus(selectedReport?.status)
  const reportList = safeItemList(reports)
  const savedRepoList = safeItemList(savedRepos)

  useEffect(() => {
    if (!setAssistantRepo) return undefined
    const context = projectHealthAssistantContext(selectedReport)
    if (!context) return undefined
    setAssistantRepo(context)
    return () => {
      setAssistantRepo((current) => (
        current?.reportId === context.reportId ? null : current
      ))
    }
  }, [selectedReport, setAssistantRepo])

  useEffect(() => (
    setPageMetadata({
      title: `${t('projectHealth.title')} - ${t('nav.brand')}`,
      description: t('projectHealth.description'),
    })
  ), [t])

  useEffect(() => () => window.clearTimeout(feedbackTimerRef.current), [])

  useEffect(() => {
    if (incomingRepoUrl) setRepoUrl(incomingRepoUrl)
  }, [incomingRepoUrl])

  const loadReports = useCallback(async (signal, options = {}) => {
    if (!user) return []
    if (!options.silent) setLoadingReports(true)
    setReportsError('')
    try {
      const payload = await getProjectHealthReports(signal)
      const items = reportItems(payload)
      setAnalysisUsage(analysisUsageFromPayload(payload, user))
      setReports(items)
      return items
    } catch (error) {
      if (error.name === 'AbortError') return []
      setAnalysisUsage(DEFAULT_ANALYSIS_USAGE)
      setReports([])
      setReportsError(error.message || t('projectHealth.loadHistoryFailed'))
      return []
    } finally {
      if (!options.silent) setLoadingReports(false)
    }
  }, [t, user])

  const loadSavedRepos = useCallback(async (signal) => {
    if (!user) return []
    setSavedReposError('')
    try {
      const payload = await getMyGithubRepositories(signal)
      const items = reportItems(payload)
      setSavedRepos(items)
      if (payload?.ok === false) {
        setSavedReposError(SAVED_REPOS_LOAD_ERROR)
      }
      return items
    } catch (error) {
      if (error.name !== 'AbortError') {
        setSavedReposError(SAVED_REPOS_LOAD_ERROR)
      }
      setSavedRepos([])
      return []
    }
  }, [user])

  const loadReportDetail = useCallback(async (reportId, signal, options = {}) => {
    if (!user || !reportId) return
    if (!options.silent) {
      setDetailLoading(true)
      setDetailError('')
    }
    try {
      const payload = await getProjectHealthReport(reportId, signal)
      const report = projectHealthReportFromPayload(payload)
      setSelectedReport((current) => (
        report || (String(current?.id || '') === String(reportId) ? current : null)
      ))
    } catch (error) {
      if (error.name !== 'AbortError' && !options.silent) {
        setDetailError(error.message || t('projectHealth.loadDetailFailed'))
        setSelectedReport(null)
      }
    } finally {
      if (!options.silent) setDetailLoading(false)
    }
  }, [t, user])

  useEffect(() => {
    if (!user) {
      setReports([])
      setSavedRepos([])
      setAnalysisUsage(DEFAULT_ANALYSIS_USAGE)
      return undefined
    }
    const controller = new AbortController()
    loadReports(controller.signal)
    loadSavedRepos(controller.signal)
    return () => controller.abort()
  }, [loadReports, loadSavedRepos, user])

  useEffect(() => {
    if (!user || selectedId || reportList.length === 0) return
    setSelectedId(String(reportList[0].id))
  }, [reportList, selectedId, user])

  useEffect(() => {
    if (!user || !selectedId) {
      setSelectedReport(null)
      return undefined
    }
    const controller = new AbortController()
    loadReportDetail(selectedId, controller.signal)
    return () => controller.abort()
  }, [loadReportDetail, selectedId, user])

  useEffect(() => {
    if (!user || !selectedId || selectedReportStatus !== 'pending' || pollTimedOut) {
      if (selectedReportStatus && selectedReportStatus !== 'pending') {
        pollStateRef.current = { id: '', attempts: 0 }
        setPollTimedOut(false)
      }
      return undefined
    }

    if (pollStateRef.current.id !== String(selectedId)) {
      pollStateRef.current = { id: String(selectedId), attempts: 0 }
      setPollTimedOut(false)
    }

    let intervalId
    const poll = async () => {
      if (pollStateRef.current.attempts >= POLL_MAX_ATTEMPTS) {
        setPollTimedOut(true)
        window.clearInterval(intervalId)
        return
      }
      pollStateRef.current = {
        id: String(selectedId),
        attempts: pollStateRef.current.attempts + 1,
      }
      await loadReportDetail(selectedId, undefined, { silent: true })
      await loadReports(undefined, { silent: true })
    }

    intervalId = window.setInterval(poll, POLL_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [
    loadReportDetail,
    loadReports,
    pollTimedOut,
    selectedId,
    selectedReportStatus,
    user,
  ])

  const selectedReportId = useMemo(() => String(selectedId || selectedReport?.id || ''), [
    selectedId,
    selectedReport?.id,
  ])

  const createReportFromInput = useCallback(async (value, options = {}) => {
    const normalizedValue = normalizeGithubRepoInput(value)
    setCreateError('')
    setLimitError(null)
    setNotice('')
    if (!normalizedValue) {
      setCreateError(t('projectHealth.invalidRepoFormat'))
      recordAnalyticsEvent('pet_analyze_project_failed', {
        repoUrl: value,
        reason: 'invalid_repo_url',
        sourceType: options.project?.sourceType || '',
      }).catch(() => {})
      return ''
    }
    setRepoUrl(normalizedValue)
    setCreating(true)
    try {
      const response = await createProjectHealthReport(normalizedValue, { language })
      const payload = response?.data ?? response ?? {}
      const report = payload?.report ?? null
      const reportId = payload?.reportId ?? report?.id ?? null
      const status = payload?.status ?? report?.status ?? ''
      setAnalysisUsage(analysisUsageFromPayload(response, user))
      const safeReport = projectHealthReportFromPayload(response, normalizedValue)
      const safeReportId = reportId ? String(reportId) : ''
      if (safeReportId) rewardCandidateReportsRef.current.add(safeReportId)
      if (safeReport) {
        setSelectedReport(safeReport)
        setReports((current) => {
          const currentReports = safeItemList(current)
          const exists = currentReports.some((item) => String(item?.id) === String(safeReport.id))
          return exists
            ? currentReports.map((item) => (
              String(item?.id) === String(safeReport.id) ? { ...item, ...safeReport } : item
            ))
            : [safeReport, ...currentReports]
        })
      }
      pollStateRef.current = { id: '', attempts: 0 }
      setPollTimedOut(false)
      setNotice(
        String(status).toLowerCase() === 'pending'
          ? '分析任务已创建，正在生成报告...'
          : payload?.existing
          ? t('projectHealth.existingNotice')
          : t('projectHealth.createNotice'),
      )
      await loadReports(undefined, { silent: true })
      if (safeReportId) setSelectedId(safeReportId)
      return safeReportId
    } catch (error) {
      setAnalysisUsage(DEFAULT_ANALYSIS_USAGE)
      if (isFeatureLimitExceeded(error)) {
        setLimitError(error)
        setCreateError('')
      } else {
        setCreateError(projectHealthCreateError(error, t))
      }
      recordAnalyticsEvent('pet_analyze_project_failed', {
        repoUrl: normalizedValue,
        reason: error.code || error.message || 'create_failed',
        sourceType: options.project?.sourceType || '',
      }).catch(() => {})
      return ''
    } finally {
      setCreating(false)
    }
  }, [language, loadReports, t, user])

  async function handleCreate(event) {
    event.preventDefault()
    await createReportFromInput(repoUrl, { trigger: 'manual' })
  }

  useEffect(() => {
    if (!incomingRepoUrl) return
    const normalizedValue = normalizeGithubRepoInput(incomingRepoUrl)
    if (!normalizedValue) {
      setCreateError(t('projectHealth.incomingRepoInvalid'))
      return
    }
    setRepoUrl(normalizedValue)
    if (!shouldAutoAnalyze || authLoading) return

    const key = `${normalizedValue}:auto`
    if (autoAnalyzeRef.current === key) return
    autoAnalyzeRef.current = key
    createReportFromInput(normalizedValue, {
      project: location.state?.petProject,
      trigger: 'external',
    })
  }, [
    authLoading,
    createReportFromInput,
    incomingRepoUrl,
    location.state?.petProject,
    shouldAutoAnalyze,
    t,
  ])

  useEffect(() => {
    if (!user || !selectedReport?.id || !selectedReport?.status) return
    const reportId = String(selectedReport.id)
    if (!rewardCandidateReportsRef.current.has(reportId)) return

    const status = selectedReportStatus
    if (status === 'failed' && !trackedFinishedReportsRef.current.has(reportId)) {
      trackedFinishedReportsRef.current.add(reportId)
      recordAnalyticsEvent('pet_analyze_project_failed', {
        reportId,
        repoUrl: selectedReport.repoUrl || selectedReport.repoFullName,
        sourceType: location.state?.petProject?.sourceType || '',
      }).catch(() => {})
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ai-pet:analysis-failed', {
          detail: {
            reportId,
            repoUrl: selectedReport.repoUrl || selectedReport.repoFullName || '',
          },
        }))
      }
      return
    }

    if (status !== 'completed' || rewardedReportsRef.current.has(reportId)) return
    rewardedReportsRef.current.add(reportId)
    trackedFinishedReportsRef.current.add(reportId)
    recordAnalyticsEvent('pet_analyze_project_success', {
      reportId,
      repoUrl: selectedReport.repoUrl || selectedReport.repoFullName,
      sourceType: location.state?.petProject?.sourceType || '',
    }).catch(() => {})
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ai-pet:analysis-success', {
        detail: {
          reportId,
          repoUrl: selectedReport.repoUrl || selectedReport.repoFullName || '',
        },
      }))
    }
    rewardPetTask({
      rewardFood: 2,
      targetId: selectedReport.repoUrl || selectedReport.repoFullName || reportId,
      targetType: 'repo',
      taskKey: 'github_analysis',
    })
      .then((result) => {
        if (result.rewarded) {
          toast.success(result.message || '宠物获得 2 份食物')
          recordAnalyticsEvent('pet_task_reward', {
            reportId,
            rewardFood: result.rewardFood || 2,
            taskKey: 'github_analysis',
          }).catch(() => {})
        }
      })
      .catch(() => {})
  }, [location.state?.petProject?.sourceType, selectedReport, selectedReportStatus, user])

  async function handleRefresh(reportId) {
    const id = String(reportId)
    setRefreshingId(id)
    setReportsError('')
    setNotice('')
    try {
      const response = await refreshProjectHealthReport(id, { language })
      const report = projectHealthReportFromPayload(response)
      setAnalysisUsage(analysisUsageFromPayload(response, user))
      pollStateRef.current = { id: '', attempts: 0 }
      setPollTimedOut(false)
      if (report) {
        setReports((current) => safeItemList(current).map((item) => (
          String(item?.id) === id
            ? { ...item, ...report, reportJson: report.reportJson ?? item.reportJson }
            : item
        )))
        if (selectedReportId === id) setSelectedReport(report)
      }
      setSelectedId(id)
      setNotice(
        String(report?.status || '').toLowerCase() === 'pending'
          ? '分析任务已创建，正在生成报告...'
          : t('projectHealth.refreshNotice'),
      )
      await loadReports(undefined, { silent: true })
    } catch (error) {
      setAnalysisUsage(DEFAULT_ANALYSIS_USAGE)
      setReportsError(error.message || t('projectHealth.refreshFailed'))
      if (selectedReportId === id && selectedReportStatus === 'pending') {
        setNotice('分析任务已创建，正在生成报告...')
      }
    } finally {
      setRefreshingId('')
    }
  }

  async function handleSyncGithubRepos() {
    setSyncingRepos(true)
    setSavedReposError('')
    try {
      const payload = await syncMyGithubRepositories()
      setSavedRepos(reportItems(payload))
    } catch (error) {
      setSavedReposError(error.message || t('projectHealth.githubSyncFailed'))
      setSavedRepos([])
    } finally {
      setSyncingRepos(false)
    }
  }

  async function handleDelete(reportId) {
    const id = String(reportId)
    if (!window.confirm(t('projectHealth.deleteConfirm'))) return
    setDeletingId(id)
    setReportsError('')
    try {
      await deleteProjectHealthReport(id)
      setReports((current) => safeItemList(current).filter((item) => String(item?.id) !== id))
      if (selectedReportId === id) {
        setSelectedId('')
        setSelectedReport(null)
      }
      await loadReports(undefined, { silent: true })
      setNotice(t('projectHealth.deletedNotice'))
    } catch (error) {
      setReportsError(error.message || t('projectHealth.deleteFailed'))
    } finally {
      setDeletingId('')
    }
  }

  function showShareFeedback(message) {
    setShareFeedback(message)
    window.clearTimeout(feedbackTimerRef.current)
    feedbackTimerRef.current = window.setTimeout(() => setShareFeedback(''), 2600)
  }

  async function handleCopyShareLink(report) {
    try {
      await copyText(buildAnalysisShareUrl(report.id))
      showShareFeedback(t('share.copiedLink'))
    } catch {
      showShareFeedback(t('common.copyFailed'))
    }
  }

  async function handleCopyPromotionText(report) {
    try {
      const shareUrl = buildAnalysisShareUrl(report.id)
      await copyText(buildAnalysisPromotionText(report, shareUrl))
      showShareFeedback(t('repo.copiedPromotion'))
    } catch {
      showShareFeedback(t('common.copyFailed'))
    }
  }

  if (authLoading) {
    return (
      <div className="page-container project-health-page">
        <BackButton fallbackPath={fallbackPath}>{backLabel}</BackButton>
        <LoadingState label={t('projectHealth.checkingLogin')} />
      </div>
    )
  }

  return (
    <div className="page-container project-health-page">
      <BackButton fallbackPath={fallbackPath}>{backLabel}</BackButton>
      <PageHeader
        eyebrow="GitHub 项目分析"
        title={t('projectHealth.title')}
        description="粘贴仓库地址，生成简历和面试可用的项目表达。"
      />

      <div className="project-health-grid">
        <div className="project-health-stack">
          <Card className="project-health-form-card">
            <CardHeader>
              <CardTitle>{t('projectHealth.inputTitle')}</CardTitle>
              <CardDescription>
                支持 GitHub 链接或 owner/repo。
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="project-health-form" onSubmit={handleCreate}>
                <label className="project-health-field" htmlFor="project-health-repo">
                  <span>{t('projectHealth.repoAddress')}</span>
                  <div className="project-health-input-row">
                    <input
                      id="project-health-repo"
                      value={repoUrl}
                      onChange={(event) => setRepoUrl(event.target.value)}
                      placeholder={t('projectHealth.repoPlaceholder')}
                      disabled={creating}
                    />
                    <Button type="submit" disabled={creating || !analysisUsage.canAnalyze}>
                      {creating ? <LoaderCircle className="spin" size={16} /> : <Search size={16} />}
                      {creating ? t('projectHealth.creating') : t('projectHealth.start')}
                    </Button>
                  </div>
                </label>
              </form>
              {createError && (
                <p className="project-health-message is-error" role="alert">
                  <AlertTriangle size={16} />
                  {createError}
                </p>
              )}
              {limitError && <FeatureLimitCard message={limitError.message} />}
              {notice && (
                <p className="project-health-message is-success" role="status">
                  <Activity size={16} />
                  {notice}
                </p>
              )}
              <p className="project-health-usage-strip">
                <span>{analysisUsage.periodLabel || '本月'}已分析：{analysisUsage.used}</span>
                <span>{analysisUsage.periodLabel || '本月'}剩余：{formatAnalysisUsage(analysisUsage)}</span>
              </p>
              {!analysisUsage.canAnalyze && (
                <FeatureLimitCard />
              )}
            </CardContent>
          </Card>

          {!user && (
            <Card className="project-health-login-card project-health-secondary-card">
              <CardHeader>
                <CardTitle>登录后保存历史报告</CardTitle>
                <CardDescription>
                  可重新分析项目和查看历史记录。
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Button onClick={() => navigate('/login')}>
                  <LogIn size={16} />
                  {t('projectHealth.goLogin')}
                </Button>
              </CardFooter>
            </Card>
          )}

          {user && (
            <ProjectHealthSectionBoundary>
            <Card className="project-health-form-card project-health-saved-card">
            <CardHeader>
              <CardTitle>{t('projectHealth.myGithubProjects')}</CardTitle>
              <CardDescription>分析过的项目会显示在这里。</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="project-health-mini-list">
                {savedRepoList.slice(0, 5).map((repo) => {
                  const repoFullName = repo?.fullName || repo?.full_name || ''
                  const repoHtmlUrl = repo?.htmlUrl || repo?.html_url || (repoFullName ? `https://github.com/${repoFullName}` : '')
                  return (
                    <article key={repo?.id || repoFullName}>
                      <div>
                        <strong>{repoFullName || t('common.unknown')}</strong>
                        <p>{repo?.description || t('projectHealth.noReportSummary')}</p>
                        <small>{repo?.primaryLanguage || repo?.primary_language || t('common.unlabeled')} - Star {repo?.stars ?? 0}</small>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => createReportFromInput(repoHtmlUrl, { trigger: 'saved_repo' })}
                        disabled={creating}
                      >
                        {t('projectHealth.reanalyze')}
                      </Button>
                    </article>
                  )
                })}
                {savedRepoList.length === 0 && (
                  <p className="project-health-pending-note">{t('projectHealth.noSavedGithubProjects')}</p>
                )}
              </div>
              {savedReposError && <p className="project-health-message is-error">{savedReposError}</p>}
            </CardContent>
            <CardFooter>
              <Button variant="outline" onClick={handleSyncGithubRepos} disabled={syncingRepos}>
                <RefreshCw className={syncingRepos ? 'spin' : ''} size={16} />
                {t('projectHealth.syncGithubProjects')}
              </Button>
            </CardFooter>
            </Card>
            </ProjectHealthSectionBoundary>
          )}

          {user && (
            <ProjectHealthSectionBoundary>
            <section className="project-health-history" aria-labelledby="project-health-history-title">
            <div className="project-health-section-heading">
              <div>
                <span className="eyebrow">{t('projectHealth.historyEyebrow')}</span>
                <h2 id="project-health-history-title">{t('projectHealth.historyTitle')}</h2>
                <p>{t('projectHealth.historyDescription')}</p>
              </div>
            </div>

            {loadingReports && <LoadingState label={t('projectHealth.loadingHistory')} />}
            {!loadingReports && reportsError && (
              <ErrorState message={reportsError} onRetry={() => loadReports()} />
            )}
            {!loadingReports && !reportsError && reportList.length === 0 && (
              <EmptyState
                icon={<FileText size={30} />}
                title={t('projectHealth.emptyHistoryTitle')}
                message={t('projectHealth.emptyHistoryMessage')}
              />
            )}
            {!loadingReports && !reportsError && reportList.length > 0 && (
              <div className="project-health-report-list">
                {reportList.map((report, index) => {
                  if (!reportHasDisplayIdentity(report)) {
                    return (
                      <article className="project-health-report-card" key={`invalid-${index}`}>
                        <div className="project-health-report-main">
                          <div>
                            <h3>这条历史报告格式异常</h3>
                            <p>请删除后重新生成。</p>
                          </div>
                          <StatusBadge status="error" />
                        </div>
                      </article>
                    )
                  }
                  const id = String(report.id || report.repoFullName || index)
                  const reportStatus = normalizeReportStatus(report.status)
                  const active = id === selectedReportId
                  const summary = localizedDisplayText(
                    report.summary,
                    reportStatus === 'pending' ? pendingMessage : t('projectHealth.noReportSummary'),
                    language,
                  )
                  return (
                    <article
                      className={`project-health-report-card ${active ? 'is-active' : ''}`}
                      key={id}
                    >
                      <div className="project-health-report-main">
                        <div>
                          <h3>{report.repoFullName || report.repoUrl || t('common.unknown')}</h3>
                          <p>{summary}</p>
                        </div>
                        <StatusBadge status={reportStatus} />
                      </div>
                      <div className="project-health-report-meta">
                        <span>{`${t('projectHealth.score')}: ${report.score ?? '--'}`}</span>
                        <span>
                          <Clock3 size={14} />
                          创建：{formatDateTime(report.createdAt)}
                        </span>
                        <span>
                          <Clock3 size={14} />
                          更新：{formatDateTime(report.updatedAt)}
                        </span>
                      </div>
                      <div className="project-health-report-actions">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setSelectedId(id)}
                        >
                          {t('projectHealth.viewDetail')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRefresh(id)}
                          disabled={refreshingId === id}
                        >
                          <RefreshCw className={refreshingId === id ? 'spin' : ''} size={14} />
                          {reportStatus === 'error' ? '重新分析' : t('projectHealth.refresh')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(id)}
                          disabled={deletingId === id}
                        >
                          <Trash2 size={14} />
                          {t('common.delete')}
                        </Button>
                      </div>
                    </article>
                  )
                })}
              </div>
            )}
            </section>
            </ProjectHealthSectionBoundary>
          )}
        </div>

        {user && (
          <ProjectHealthSectionBoundary>
          <aside className="project-health-detail" aria-labelledby="project-health-detail-title">
          {!selectedReportId && !detailLoading && (
            <EmptyState
              icon={<FileText size={30} />}
              title={t('projectHealth.selectReport')}
              message={t('projectHealth.selectReportMessage')}
            />
          )}
          {detailLoading && <LoadingState label={t('projectHealth.loadingDetail')} />}
          {!detailLoading && detailError && (
            <ErrorState message={detailError} onRetry={() => loadReportDetail(selectedReportId)} />
          )}
          {!detailLoading && !detailError && selectedReport && (
            <Card className="project-health-detail-card">
              <CardHeader>
                <div className="project-health-detail-title">
                  <div>
                    <span className="eyebrow">{t('projectHealth.detailEyebrow')}</span>
                    <CardTitle id="project-health-detail-title">{selectedReport.repoFullName}</CardTitle>
                  </div>
                  <StatusBadge status={selectedReportStatus} />
                </div>
                <CardDescription>
                  {t('projectHealth.updatedPrefix')}{formatDateTime(selectedReport.updatedAt)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="project-health-detail-metrics">
                  <span>
                    <strong>{selectedReport.score ?? '--'}</strong>
                    {t('projectHealth.score')}
                  </span>
                  <span>
                    <strong>{formatDateTime(selectedReport.generatedAt)}</strong>
                    {t('projectHealth.generatedAt')}
                  </span>
                  <span>
                    <strong>{formatDateTime(selectedReport.refreshedAt)}</strong>
                    {t('projectHealth.refreshedAt')}
                  </span>
                </div>
                {selectedReport.summary && (
                  <section className="project-health-detail-section">
                    <h3>{t('projectHealth.summary')}</h3>
                    <p>{localizedDisplayText(selectedReport.summary, t('projectHealth.noReportSummary'), language)}</p>
                  </section>
                )}
                {selectedReport.errorMessage && (
                  <section className="project-health-detail-section is-error">
                    <h3>{t('projectHealth.errorInfo')}</h3>
                    <p>{localizedDisplayText(selectedReport.errorMessage, t('projectHealth.noReportContent'), language)}</p>
                  </section>
                )}
                {selectedReportStatus === 'pending' && (
                  <section className="project-health-detail-section">
                    <h3>{t('projectHealth.progress')}</h3>
                    <p className="project-health-pending-note">{pendingMessage}</p>
                    {pollTimedOut && (
                      <p className="project-health-pending-note is-timeout">
                        报告仍在生成中，你可以稍后刷新查看。
                      </p>
                    )}
                  </section>
                )}
                {selectedReportStatus === 'error' && (
                  <section className="project-health-detail-section is-error">
                    <h3>{t('projectHealth.errorInfo')}</h3>
                    <p>分析失败，可重新生成。</p>
                  </section>
                )}
                {selectedReportStatus === 'completed' && hasReportJson(selectedReport.reportJson) && (
                  <section className="project-health-detail-section">
                    <h3>{t('projectHealth.reportContent')}</h3>
                    <ProjectHealthReportView reportJson={selectedReport.reportJson} canViewVipSections={canViewVipSections} />
                  </section>
                )}
                {selectedReportStatus === 'completed' && !hasReportJson(selectedReport.reportJson) && (
                  <section className="project-health-detail-section">
                    <h3>{t('projectHealth.reportContent')}</h3>
                    <p className="project-health-pending-note">
                      这条历史报告格式异常，请重新生成。
                    </p>
                  </section>
                )}
                {selectedReportStatus !== 'pending'
                  && selectedReportStatus !== 'completed'
                  && selectedReportStatus !== 'error'
                  && !selectedReport.errorMessage && (
                    <section className="project-health-detail-section">
                      <h3>{t('projectHealth.reportContent')}</h3>
                      <p className="project-health-pending-note">
                        {t('projectHealth.noReportContent')}
                      </p>
                    </section>
                  )}
              </CardContent>
              <CardFooter className="project-health-detail-actions">
                {selectedReportStatus === 'completed' && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => handleCopyShareLink(selectedReport)}
                    >
                      <Link2 size={16} />
                      {t('projectHealth.copyShare')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleCopyPromotionText(selectedReport)}
                    >
                      {shareFeedback === t('repo.copiedPromotion') ? <Check size={16} /> : <Copy size={16} />}
                      {t('projectHealth.copyPromotion')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setPosterOpen(true)}
                    >
                      <Image size={16} />
                      {t('projectHealth.poster')}
                    </Button>
                    <Link
                      className="secondary-button"
                      to={`/github-analysis/share/${selectedReport.id}`}
                    >
                      <Share2 size={16} />
                      {t('projectHealth.openShare')}
                    </Link>
                  </>
                )}
                <Button
                  variant="secondary"
                  onClick={() => handleRefresh(selectedReport.id)}
                  disabled={refreshingId === String(selectedReport.id)}
                >
                  <RefreshCw
                    className={refreshingId === String(selectedReport.id) ? 'spin' : ''}
                    size={16}
                  />
                  {selectedReportStatus === 'error' ? '重新分析' : t('projectHealth.refreshReport')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(selectedReport.id)}
                  disabled={deletingId === String(selectedReport.id)}
                >
                  <Trash2 size={16} />
                  {t('projectHealth.deleteReport')}
                </Button>
              </CardFooter>
              {shareFeedback && (
                <p className="project-health-share-feedback" role="status">
                  {shareFeedback}
                </p>
              )}
              {selectedReportStatus === 'completed' && (
                <FeedbackBox
                  compact
                  page="github-analysis-result"
                  reportId={selectedReport.id}
                  repoUrl={selectedReport.repoUrl || selectedReport.repoFullName}
                />
              )}
            </Card>
          )}
          </aside>
          </ProjectHealthSectionBoundary>
        )}
      </div>
      <section className="project-health-secondary-strip">
        <MembershipStatusCard />
        <FeedbackBox
          compact
          page="github-analysis"
          reportId={selectedReportId}
          repoUrl={selectedReport?.repoUrl || repoUrl}
        />
      </section>
      {posterOpen && selectedReport && (
        <SharePosterModal
          report={selectedReport}
          onClose={() => setPosterOpen(false)}
        />
      )}
    </div>
  )
}

export default ProjectHealthPage
